<script setup>
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import i18next from 'i18next';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { fetchData } from '../../fetchData.js';
import Dropdown from '../Dropdown.vue';
import ContentStreamItemV2 from './ContentStreamItemV2.vue';
import ScrollMoreButton from '../ScrollMoreButton.vue';

const props = defineProps({
    showIndicator: {
        type: Boolean,
        default: false
    },
    productId: {
        type: String,
        default: ''
    },
    showDropdown: {
        type: Boolean,
        default: true
    },
    pageSize: {
        type: Number,
        default: 20
    },
    initFilter: {
        type: Array,
        required: false,
        default: () => []
    }
});

const language = i18next.language || 'de';

const page = ref(1);
const datepickerOpen = ref(false);
const lastPage = ref(1);
const appendToList = ref(false);
const searchPhrase = ref('');
const dateRange = ref([null, null]);
const dropdownValues = ref([]);
const selectedDropdownValue = ref(null);
const contentStreams = ref([]);
const isFavorite = ref(false);
const showIndicatorCheckbox = ref(false);
const isIndicator = ref(false);
const contentstreamTypeId = ref([]);

const sentinel = ref(null);
const observer = ref(null);

const maxDate = ref(new Date());

const createObserver = () => {
    if (observer.value) observer.value.disconnect();

    observer.value = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting && page.value < lastPage.value) {
                loadNextPage();
            }
        });
    });

    if (sentinel.value) {
        observer.value.observe(sentinel.value);
    }
};

const getContentsteamTypes = async () => {
    try {
        const url = '/api/v1/contentstream-types';
        const { result = [] } = await fetchData(url);
        dropdownValues.value = result.data
            .filter((item) => item.visible === true)
            .filter((item) => item.title.en !== 'Email marketing')
            .map((item) => ({
                key: item.id,
                name: item.name,
                title: item.title[i18next.language] || item.title.de
            }));
    } catch (e) {
        console.error('Error fetching data:', e);
    }
};

const getData = async () => {
    const API = `/api/v2/contentstreamList`;
    try {
        let url = `${API}?sort[by]=published_at&sort[order]=desc&page_size=${props.pageSize}&page=${page.value}`;

        if (searchPhrase.value) {
            url += `&search_phrase=${encodeURIComponent(searchPhrase.value)}`;
        }

        if (props.productId) {
            url += `&product_id[]=${props.productId}`;
        }

        if (isFavorite.value) {
            url += `&is_favorite=true`;
        }

        if (isIndicator.value || props.showIndicator) {
            url += `&indicator_analysis=true`;
        }

        if (contentstreamTypeId.value.length > 0) {
            contentstreamTypeId.value.forEach((typeId) => {
                url += `&contentstream_type_id[]=${typeId}`;
            });
        }

        if (dateRange.value[0] && dateRange.value[1]) {
            url += `&published_at[]=${dateRange.value[0]}`;
            url += `&published_at[]=${dateRange.value[1]}`;
        }

        const { result = [] } = await fetchData(url);

        if (appendToList.value) {
            contentStreams.value = [...contentStreams.value, ...result.data];
        } else {
            contentStreams.value = result.data;
        }

        page.value = result.meta?.current_page || 1;
        lastPage.value = result.meta?.last_page || 1;
        await nextTick();
        window.dispatchEvent(new Event('resize'));
    } catch (e) {
        console.error('Error fetching data:', e);
    }
};

onMounted(async () => {
    await getContentsteamTypes();

    if (Array.isArray(props.initFilter) && props.initFilter.length > 0) {
        const matchingOptions = dropdownValues.value.filter((item) => props.initFilter.includes(item.name));

        if (matchingOptions.length === 1) {
            selectedDropdownValue.value = matchingOptions[0].key;
            contentstreamTypeId.value = [matchingOptions[0].key];
        } else if (matchingOptions.length >= 1) {
            contentstreamTypeId.value = matchingOptions.map((item) => item.key);
        }
    }

    await getData();
    createObserver();
});

onBeforeUnmount(() => {
    if (observer.value) {
        observer.value.disconnect();
    }
});

const loadNextPage = async () => {
    page.value += 1;
    appendToList.value = true;
    await getData();
};

const handleDropdownChange = (value) => {
    appendToList.value = false;

    const matchingOption = dropdownValues.value.find((item) => item.title === 'Tägliches Marktupdate' || item.title === 'Daily Market Update');

    if (matchingOption && matchingOption.key === value) {
        showIndicatorCheckbox.value = true;
    } else {
        showIndicatorCheckbox.value = false;
        isIndicator.value = false;
    }

    if (value === null || value === 'Alle' || value === 'All') {
        contentstreamTypeId.value = [];
        selectedDropdownValue.value = null;
    } else {
        selectedDropdownValue.value = value;
        contentstreamTypeId.value = [value];
    }

    getData();
};

const debounce = (func, delay) => {
    let timeout;

    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
};

const handleSearchInput = debounce((value) => {
    searchPhrase.value = value;
    appendToList.value = false;
    getData();
}, 300);

const clearSearch = () => {
    searchPhrase.value = '';
    appendToList.value = false;
    getData();
};

const closeDatepicker = () => {
    datepickerOpen.value = false;
};

const handleDateChange = () => {
    closeDatepicker();
    appendToList.value = false;
    getData();
};

const toggleDatepicker = () => {
    if (dateRange.value[0] && dateRange.value[1]) {
        dateRange.value = [null, null];
        appendToList.value = false;
        getData();
    } else {
        datepickerOpen.value = !datepickerOpen.value;
    }
};
const refetchData = () => {
    appendToList.value = false;
    page.value = 1;
    getData();
};
const changeFavoriteState = () => {
    appendToList.value = false;
    page.value = 1;
    getData();
};

const resetFilters = () => {
    searchPhrase.value = '';
    dateRange.value = [null, null];
    selectedDropdownValue.value = null;
    contentstreamTypeId.value = [];
    isFavorite.value = false;
    appendToList.value = false;
    page.value = 1;
    getData();
};
</script>

<template>
    <div>
        <div class="flex justify-between bg-white p-4 align-text-bottom rounded-t-lg">
            <div class="flex justify-start items-center">
                <p class="text-base font-bold mt-2 ml-2">{{ $t('nav_analysis') }}</p>
            </div>
            <RouterLink
                to="/dashboard/markets"
                class="text-sm text-gray-500 hover:text-blue-dark flex items-center justify-center gap-1 border border-gray-300 hover:border-blue-dark px-3 py-1 rounded-lg transition duration-300 h-full"
                style="align-items: center"
            >
                <p class="flex items-center h-full">{{ $t('all_markets') }}</p>
                <i class="fi fi-rr-arrow-right text-xs flex items-center h-full ml-2"></i>
            </RouterLink>
        </div>

        <div class="relative bg-white rounded-b-lg pb-4 px-6" :class="pageSize === 20 ? 'max-h-[600px]' : 'max-h-full'">
            <div class="flex items-center gap-2 mb-2">
                <div class="relative flex-1">
                    <input
                        v-model="searchPhrase"
                        type="text"
                        :placeholder="$t('plain_search')"
                        class="block w-full px-3 py-2 border rounded-lg bg-dashboard-primary shadow-sm hover:shadow-md focus:outline-none text-xs duration-300"
                        @input="handleSearchInput($event.target.value)"
                    />
                    <i v-if="!searchPhrase" class="fi fi-rr-search absolute top-1/2 right-3 transform -translate-y-1/2"></i>
                    <i v-else class="fi fi-rr-cross-circle absolute top-1/2 right-3 transform -translate-y-1/2 cursor-pointer hover:text-gray-600" @click="clearSearch"></i>
                </div>
                <button class="p-2 bg-dashboard-primary rounded-lg shadow-sm hover:bg-gray-200 transition duration-200 flex items-center justify-center" @click="toggleDatepicker">
                    <i class="fi fi-rr-calendar"></i>
                </button>
            </div>

            <div :class="['absolute shadow-xl duration-300 z-20 right-0', datepickerOpen ? 'block' : 'hidden']" style="top: 60px" @click.outside="closeDatepicker">
                <Datepicker
                    v-model="dateRange"
                    :model-type="language === 'de' ? 'dd.MM.yyyy' : 'yyyy-MM-dd'"
                    :locale="language === 'de' ? 'de-DE' : 'en-GB'"
                    class="!m-0"
                    :select-text="$t('plain_select')"
                    :enable-time-picker="false"
                    :range="true"
                    inline
                    auto-apply
                    :monday-first="true"
                    :max-date="maxDate"
                    @update:model-value="handleDateChange"
                />
            </div>

            <Dropdown
                v-if="showDropdown"
                class="mb-4"
                :dropdown-values="dropdownValues"
                :selected-dropdown-value="selectedDropdownValue"
                @update:selected-dropdown-value="selectedDropdownValue = $event"
                @change="handleDropdownChange"
            />

            <div class="flex flex-row flex-wrap items-center gap-2 mb-4">
                <div class="flex items-center gap-2 bg-dashboard-primary px-3 py-2 rounded-lg shadow-sm hover:shadow-md duration-300">
                    <input
                        id="favoriteSwitch"
                        v-model="isFavorite"
                        type="checkbox"
                        class="h-5 w-5 text-blue focus:ring-blue/70 border-blue-500 rounded-lg"
                        @change="changeFavoriteState"
                    />
                    <label for="favoriteSwitch" class="text-xs">{{ $t('plain_favourites') }}</label>
                </div>

                <div v-if="showIndicatorCheckbox" class="flex items-center gap-2 bg-dashboard-primary px-3 py-2 rounded-lg shadow-sm hover:shadow-md duration-300">
                    <input
                        id="isIndicatorSwitch"
                        v-model="isIndicator"
                        type="checkbox"
                        class="h-5 w-5 text-blue focus:ring-blue/70 border-gray-300 rounded-lg"
                        @change="changeFavoriteState"
                    />
                    <label for="isIndicatorSwitch" class="text-xs">{{ $t('indicator_analysis') }}</label>
                </div>
                <div v-if="dateRange[0]" class="flex text-xs px-3 py-2 rounded-lg border border-blue text-blue">
                    <div v-if="dateRange[0]">
                        <span>{{ dateRange[0] }}</span>
                    </div>
                    <span v-if="dateRange[0] && dateRange[1]"> - </span>
                    <div v-if="dateRange[1]">
                        <span>{{ dateRange[1] }}</span>
                    </div>
                </div>
            </div>
            <div>
                <div class="relative">
                    <div data-scroll-container class="overflow-y-scroll h-full pb-14 scroll-smooth" :class="pageSize === 20 ? 'max-h-[350px]' : 'max-h-[500px]'">
                        <div v-for="contentStream in contentStreams" :key="contentStream.id">
                            <ContentStreamItemV2 :content-stream="contentStream" :language="language" class="my-3" @refetch-query="refetchData()" />
                        </div>

                        <div v-if="(contentStreams?.length || 0) === 0" class="text-center mt-4">
                            <button class="px-4 py-2 border border-blue text-blue rounded-lg hover:bg-blue/70 hover:text-white transition duration-300" @click="resetFilters">
                                {{ $t('reset_filters') }}
                            </button>
                        </div>

                        <div ref="sentinel" class="h-12 mt-4 flex justify-center items-center text-gray-400">
                            <span v-if="page < lastPage">{{ $t('loading') }}...</span>
                        </div>
                    </div>
                    <ScrollMoreButton />
                </div>
            </div>
        </div>
    </div>
</template>
